'use client';

import React from 'react';
import { useWizard } from '@app/app/_components/Wizard/useWizard';
import { Wizard, WizardStep } from '@app/app/_components/Wizard/Wizard';
import { GatewayRedirectStep } from '@app/app/esales/transport/_sections/steps/GatewayRedirectStep';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useBeforeUnload } from '@app/lib/use-before-unload/useBeforeUnload';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormState } from 'react-dom';
import { scrollToTop } from '@app/lib/scroll-to-top/scrollToTop';
import { ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { buildSteps } from '@app/app/esales/travel/_sections/TravelWizardSection/helpers/buildSteps';
import { createEnhancedFormAction } from '@app/app/esales/travel/_sections/TravelWizardSection/helpers/createEnhancedFormAction';
import { validateStep } from '@app/app/esales/travel/_sections/TravelWizardSection/helpers/validateStep';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { useTravelWizardCallbacks } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/useTravelWizardCallbacks';
import { useInsuredPersonsState } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/useInsuredPersonsState';
import { usePolicyholderFormFields } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/usePolicyholderFormFields';
import { usePriceCalculation } from '@app/app/esales/travel/_sections/setups/usePriceCalculation';
import { TravelPremiumResponse } from '@app/domain/model/eshop/travel/TravelPremiumResponse';
import { TravelWizardFooter } from '@app/app/esales/travel/_sections/TravelWizardSection/TravelWizardFooter';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Client } from '@app/domain/model/Client';
import { useWizardStepNavigation } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/useWizardStepNavigation';
import { useTravelWizardComputedValues } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/useTravelWizardComputedValues';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';
import { clearSessionId } from '@app/infrastructure/eshop/clearSessionId';
import { TRAVEL_INSURANCE_STEPS } from '@app/app/esales/travel/_sections/steps/TRAVEL_INSURANCE_STEPS';
import { TravelLandingSection } from '@app/app/esales/travel/_sections/TravelLandingSection';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { ProductDataTerritories } from '@app/domain/model/eshop/transport/ProductDataTerritories';
import { TravelInsuranceRequestResult } from '@app/service/eshop/travel/actions/requestTravelInsurance';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { twMerge } from 'tailwind-merge';
import { useTravelGtmTracking } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/useTravelGtmTracking';
import { trackStartCalculation } from '@app/infrastructure/gtm/travel/trackStartCalculation';
import { trackProposalCalculated } from '@app/infrastructure/gtm/travel/trackProposalCalculated';

interface Props {
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    purposes: ProductDataPurpose;
    territories: ProductDataTerritories;
    authState: AuthState;
    clients: Client[];
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    initialPurchaseResult?: Nullable<PurchaseTravelInsuranceResult>;
    initialStep?: Nullable<string>;
    requestData: Nullable<TravelInsuranceRequestResult>;
}

export function TravelWizardSection({
    authState,
    clients,
    swedbankLogoSrc,
    sebLogoSrc,
    initialPurchaseResult,
    initialStep,
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    purposes,
    territories,
    requestData,
}: Props) {
    const t = useTranslations();
    const formRef = useRef<HTMLFormElement>(null);
    const setCurrentStepKeyRef = useRef<((key: string) => void) | null>(null);
    const parameters = useAppParameters();

    const isReinitializedSession = initialPurchaseResult != null && initialStep != null && requestData != null;

    const [requestResult, setRequestResult] = useState<TravelInsuranceRequestResult>(
        requestData ?? {
            beginDate: '',
            endDate: '',
            goalOfTheJourney: {
                name: '',
                code: '',
            },
            territory: {
                name: '',
                code: '',
            },
        },
    );

    const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<Nullable<string>>('');

    const [finished, setFinished] = useState(false);
    const [wizardStarted, setWizardStarted] = useState(isReinitializedSession);
    const [discountCodeData, setDiscountCodeData] = useState<DiscountCodeData>({
        discountCode: '',
        isInvalid: false,
        isSuccess: false,
        validTravelCode: undefined,
    });

    const [currentPremiums, setCurrentPremiums] = useState<Nullable<TravelPremiumResponse>>(
        initialPurchaseResult?.premiums ?? null,
    );
    const [isCalculatingPremiums, setIsCalculatingPremiums] = useState(false);
    const [loaderText, setLoaderText] = useState('');
    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);
    const [clientValidationErrorMessage, setClientValidationErrorMessage] = useState<Nullable<string>>(null);

    const { handleDiscountCode, handleApplyDiscountClick } = useTravelWizardCallbacks(
        discountCodeData,
        setDiscountCodeData,
    );

    const initialInsuredPersons = initialPurchaseResult?.formData?.insuredPersons;
    const initialSelectedRiskCodes =
        initialPurchaseResult?.formData?.selectedRisks?.map(risk => risk.code || '') ?? undefined;

    const {
        insuredPersons,
        setInsuredPersons,
        handleAddPerson,
        handleRemovePerson,
        handleAgeChange,
        handleNameChange,
        handleSurnameChange,
        handlePersonalCodeChange,
        updatePrimaryPolicyholderTravelerData,
        revertPrimaryPolicyholderTravelerData,
    } = useInsuredPersonsState(initialInsuredPersons);

    const onSaveTravelers = useCallback(
        (travelers: typeof insuredPersons) => {
            setInsuredPersons(travelers);
        },
        [setInsuredPersons],
    );

    const { priceState, handleRisksChange, initializeFromSession } = usePriceCalculation(
        currentPremiums,
        initialSelectedRiskCodes,
    );

    const { trackStepCompletion } = useTravelGtmTracking({
        selectedRisks: priceState.selectedRisks,
    });

    const enhancedFormAction = createEnhancedFormAction(
        discountCodeData,
        insuredPersons,
        requestResult,
        priceState.selectedRisks,
        isReinitializedSession,
    );

    const [formState, formAction] = useFormState(enhancedFormAction, {
        status: FormStatus.Pending,
        data: initialPurchaseResult ?? undefined,
    });

    const {
        fields: policyholderFields,
        handlers: policyholderHandlers,
        setFields,
    } = usePolicyholderFormFields(authState);

    const onSavePolicyholder = useCallback(
        (data: typeof policyholderFields) => {
            setFields(data);
        },
        [setFields],
    );

    const {
        onSuccessfulLogin,
        onChangeNumberOfPersons,
        onEditPolicyholderInfo,
        changeAgeOfTraveler,
        editTravelers,
        editTravelConditions,
        editPolicy,
    } = useWizardStepNavigation({
        setCurrentStepKey: (key: string) => {
            if (setCurrentStepKeyRef.current) {
                setCurrentStepKeyRef.current(key);
            }
        },
        setWizardStarted,
    });

    const handlePaymentMethodTypeChange = useCallback(
        (paymentMethodType: Nullable<string>) => {
            setSelectedPaymentMethodType(paymentMethodType);
        },
        [setSelectedPaymentMethodType],
    );

    const { isCompany, shouldSkipInsuredPersonsStep, mergedFormState } = useTravelWizardComputedValues({
        formState,
        insuredPersons,
        clientValidationErrors,
    });

    const steps = useMemo<WizardStep[]>(
        () =>
            buildSteps({
                t,
                discountCodeData,
                handleDiscountCode,
                handleApplyDiscountClick,
                insuredPersons,
                handleAddPerson,
                handleRemovePerson,
                handleAgeChange,
                formState: mergedFormState,
                currentPremiums,
                handleRisksChange,
                priceState,
                authState,
                clients,
                swedbankLogoSrc,
                sebLogoSrc,
                onSuccessfulLogin,
                isCompany,
                selectedPaymentMethodType,
                handlePaymentMethodTypeChange,
                handleNameChange,
                handleSurnameChange,
                handlePersonalCodeChange,
                onChangeNumberOfPersons,
                onEditPolicyholderInfo,
                changeAgeOfTraveler,
                editTravelers,
                policyholderFields,
                policyholderHandlers,
                onSavePolicyholder,
                onSaveTravelers,
                requestResult,
                editTravelConditions,
                editPolicy,
                settings: parameters.settings,
                shouldSkipInsuredPersonsStep,
            }),
        [
            t,
            discountCodeData,
            handleDiscountCode,
            handleApplyDiscountClick,
            insuredPersons,
            handleAddPerson,
            handleRemovePerson,
            handleAgeChange,
            mergedFormState,
            currentPremiums,
            handleRisksChange,
            priceState,
            authState,
            clients,
            swedbankLogoSrc,
            sebLogoSrc,
            onSuccessfulLogin,
            isCompany,
            handleNameChange,
            handleSurnameChange,
            handlePersonalCodeChange,
            onChangeNumberOfPersons,
            onEditPolicyholderInfo,
            changeAgeOfTraveler,
            editTravelers,
            policyholderFields,
            policyholderHandlers,
            onSavePolicyholder,
            onSaveTravelers,
            requestResult,
            editTravelConditions,
            editPolicy,
            handlePaymentMethodTypeChange,
            selectedPaymentMethodType,
            parameters.settings,
            shouldSkipInsuredPersonsStep,
        ],
    );

    const {
        filteredSteps,
        currentSubStepIndex,
        currentStepIndex,
        currentStepKey,
        isFinalStep,
        isFirstStep,
        next,
        back,
        setCurrentStepKey,
    } = useWizard(steps);

    useEffectOnce(() => {
        if (initialSelectedRiskCodes && initialSelectedRiskCodes.length > 0) {
            initializeFromSession(initialSelectedRiskCodes);
        }

        if (initialStep) {
            setCurrentStepKey(initialStep);
        }
    });

    useChangeEffect(() => {
        if (formState.data?.premiums) {
            setCurrentPremiums(formState.data?.premiums);
        }
    }, [formState.data?.premiums]);

    useChangeEffect(() => {
        setCurrentStepKeyRef.current = setCurrentStepKey;
    }, [setCurrentStepKey]);

    useBeforeUnload(finished, t('general.areYouSureYouWantToLeave'));

    const handleBack = useCallback(() => {
        if (!wizardStarted) {
            confirm(t('general.areYouSureYouWantToLeave'));
        }

        if (isFirstStep && wizardStarted) {
            setWizardStarted(false);
        }

        formState.status = FormStatus.Pending;
        setClientValidationErrors(null);
        setClientValidationErrorMessage(null);
        back();
    }, [back, formState, isFirstStep, t, wizardStarted]);

    const handleSubmit = useCallback(async () => {
        setClientValidationErrors(null);
        setClientValidationErrorMessage(null);

        if (
            currentStepKey === TRAVEL_INSURANCE_STEPS.TRAVELERS_SETUP ||
            currentStepKey === TRAVEL_INSURANCE_STEPS.SUMMARY
        ) {
            const formData = new FormData(formRef.current!);

            try {
                await validateStep({
                    currentStepKey,
                    formData,
                    insuredPersons,
                    personalCodeFallback: formState.data?.formData?.personalCode ?? '',
                });

                if (currentStepKey === TRAVEL_INSURANCE_STEPS.TRAVELERS_SETUP) {
                    setIsCalculatingPremiums(true);
                    setLoaderText(t('eshop.travel.calculatingPremiums'));
                }

                if (currentStepKey === TRAVEL_INSURANCE_STEPS.SUMMARY) {
                    setIsCalculatingPremiums(true);
                    setLoaderText(t('eshop.travel.preparingOffer'));
                }

                formRef.current?.requestSubmit();
            } catch (error) {
                if (error instanceof ValidationError) {
                    const violations = buildViolations(error, 'general.validationError');
                    setClientValidationErrors(violations.violations ?? null);
                    setClientValidationErrorMessage('general.validationError');
                    scrollToTop();
                }
            }
        } else {
            formRef.current?.requestSubmit();
        }
    }, [currentStepKey, t, insuredPersons, formState]);

    const handleStartWizard = useCallback((result: TravelInsuranceRequestResult) => {
        setRequestResult(result);
        setWizardStarted(true);
        trackStartCalculation({
            beginDate: result.beginDate,
            endDate: result.endDate,
            territoryCode: result.territory.code,
            goalCode: result.goalOfTheJourney.code,
        });
    }, []);

    useChangeEffect(async () => {
        if (formState.status === FormStatus.Success) {
            setIsCalculatingPremiums(false);
            setClientValidationErrors(null);
            setClientValidationErrorMessage(null);

            if (currentStepKey === TRAVEL_INSURANCE_STEPS.SUMMARY && formState.data?.offers) {
                trackProposalCalculated({
                    offers: formState.data.offers,
                    selectedRisks: priceState.selectedRisks,
                });
            }

            trackStepCompletion({ currentStepKey });

            if (isFinalStep) {
                await clearSessionId();
                setFinished(true);
            } else if (currentStepKey === TRAVEL_INSURANCE_STEPS.POLICYHOLDER_INFORMATION) {
                if (formState.data?.formData.policyholderIsTraveler) {
                    updatePrimaryPolicyholderTravelerData(
                        formState.data?.formData.policyholderFirstName || '',
                        formState.data?.formData.policyholderLastName || '',
                        formState.data?.formData.personalCode || '',
                    );
                } else {
                    revertPrimaryPolicyholderTravelerData();
                }
                next();
            } else {
                next();
            }
        }

        if (formState.status === FormStatus.Error) {
            scrollToTop();
        }

        setIsCalculatingPremiums(false);
    }, formState);

    const getStepMarginClass = useCallback(() => {
        const stepMargins: Record<string, string> = {
            [TRAVEL_INSURANCE_STEPS.TRAVELERS_SETUP]: '2xl:mb-[450px]',
            [TRAVEL_INSURANCE_STEPS.CHOOSE_INSURANCE]: '2xl:mb-[450px]',
            [TRAVEL_INSURANCE_STEPS.INSURED_PERSONS]: '2xl:mb-[600px]',
            [TRAVEL_INSURANCE_STEPS.PAYMENT.METHOD_TYPE]: 'mb-[450px] 2xl:mb-[850px]',
        };

        return stepMargins[currentStepKey] || '';
    }, [currentStepKey]);

    if (finished) {
        return <GatewayRedirectStep checkoutResult={formState.data?.checkoutResult} />;
    }

    return (
        <>
            {!wizardStarted && (
                <TravelLandingSection
                    onSuccess={handleStartWizard}
                    backgroundImageSrc={backgroundImageSrc}
                    mobileImageSrc={mobileImageSrc}
                    stakeholderLogoSrc={stakeholderLogoSrc}
                    vigLogoSrc={vigLogoSrc}
                    purposes={purposes}
                    territories={territories}
                    initialRequestData={requestResult}
                />
            )}

            {wizardStarted && (
                <>
                    <GlobalLoader isVisible={isCalculatingPremiums} text={loaderText} />
                    <form ref={formRef} action={formAction}>
                        {!isFinalStep && <input type="hidden" name="step" value={currentStepKey} />}

                        <Wizard
                            isPercentageStepper
                            steps={filteredSteps}
                            currentStepIndex={currentStepIndex}
                            currentSubStepIndex={currentSubStepIndex}
                            onSubmit={handleSubmit}
                            onBack={handleBack}
                            className={twMerge('mb-[300px] flex-1', getStepMarginClass())}
                            contentFooter={
                                <div className="mb-12">
                                    <EsalesTransportFooter
                                        stakeholderLogoSrc={stakeholderLogoSrc}
                                        vigLogoSrc={vigLogoSrc}
                                    />
                                </div>
                            }
                            error={
                                clientValidationErrorMessage
                                    ? t(clientValidationErrorMessage)
                                    : formState.status === FormStatus.Error
                                      ? t(formState.message)
                                      : undefined
                            }
                            footer={
                                <TravelWizardFooter
                                    formState={formState}
                                    totalPrice={priceState.totalPrice}
                                    currentStepKey={currentStepKey}
                                />
                            }
                        />
                    </form>
                </>
            )}
        </>
    );
}
